import AiRecordFeedbackPanel, { type AiRecordFeedbackPanelHandle } from '../program/AiRecordFeedbackPanel';
import { getClassMemberDisplayNumbers } from '../../utils/classMemberNumbers';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDirtySinceOpen } from '../../hooks/useDirtySinceOpen';
import { createPortal } from 'react-dom';
import ClassRecordMemoNoteEditor, {
  type ClassRecordMemoNoteEditorHandle,
} from '../program/ClassRecordMemoNoteEditor';
import RecordMemberGroupSelect from '../program/RecordMemberGroupSelect';
import DeleteHoldButton from '../common/DeleteHoldButton';
import EvaluationPlanAppliedPickerModal from '../evaluationPlan/EvaluationPlanAppliedPickerModal';
import AssignmentMemoLinkPickerModal from '../assignmentPoints/AssignmentMemoLinkPickerModal';
import { formatPlanSummary } from '../../types/evaluationPlan';
import { useSealedMemoTagLabels } from '../../hooks/useSealedMemoTagLabels';
import type { ClassMemberGradeLayout, ClassMemberRow } from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import {
  firstRecordGroupId,
  inferSingleClassRecordNoteGroupId,
  matchMembersByClassRecordTagKeyword,
  membersForRecordGroup,
  resolveDefaultRecordGroupId,
} from '../../utils/classMemberRecordGroupUtils';
import { loadSelectedClassGroupId } from '../../utils/selectedClassGroupPreference';
import {
  buildDefaultEvalMemberTagsNote,
  evalSerializedNoteHasNonTagText,
  renderNoteWithMemberTags,
} from '../../utils/classRecordMemoHashtags';
import {
  resolveLinkedAssignmentMemoNote,
  type AssignmentMemoMemberRef,
} from '../../utils/classRecordMemosAssignmentNotes';
import { getAssignmentPointsNote } from '../../services/assignmentPointsNoteService';
import type { AssignmentPointsNoteDoc } from '../../types/assignmentPointsNote';
import {
  parseEvaluationMemoStorage,
  saveAnnualProgressProgramRecord,
  serializeClassRecordCellStorage,
  serializeEvaluationMemoStorage,
  type AnnualProgressProgramRecordTarget,
} from '../../utils/annualProgressProgramRecordSave';
import { resolveEvaluationPlanMemoAutoLines } from '../../utils/programCellDisplayText';

export type AnnualProgressProgramRecordModalKind = 'eval' | 'class';

export type AnnualProgressProgramRecordModalRow = {
  annualCellKey: string;
  dateIso: string;
  weekdayLabel: string;
  periodLabel: string;
  timetableText: string;
  templateLabel: string;
  progressUnit: string;
  /** AI 피드백 컨텍스트용(선택) */
  progressUnitNote?: string;
  progressLearning?: string;
  progressLearningNote?: string;
  progressEvalCode: string;
  progressEvalNote: string;
  progressClassRecordKind: 'empty' | 'lesson' | 'eval_log' | 'custom';
  progressClassRecordNote: string;
  progressClassRecordMemberGroupId: string | null;
  evalRecordTarget: AnnualProgressProgramRecordTarget | null;
  classRecordTarget: AnnualProgressProgramRecordTarget | null;
  evalStorageRaw?: string;
  classStorageRaw?: string;
};

const EVAL_STATUS_OPTIONS = [
  { value: '1', label: '평가계획', tone: 'plan' as const },
  { value: '2', label: '평가중', tone: 'progress' as const },
  { value: '3', label: '평가완료', tone: 'done' as const },
];

function contextLine(row: AnnualProgressProgramRecordModalRow): string {
  const [, m, d] = row.dateIso.split('-');
  const datePart = m && d ? `${Number(m)}월${Number(d)}일` : row.dateIso;
  const subject = row.timetableText || row.templateLabel;
  return `${datePart} (${row.weekdayLabel}) ${row.periodLabel} ${subject}`.trim();
}

const AnnualProgressProgramRecordModals: React.FC<{
  openKind: AnnualProgressProgramRecordModalKind | null;
  row: AnnualProgressProgramRecordModalRow | null;
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  memberLayout: ClassMemberGradeLayout;
  classMembers: ClassMemberRow[];
  onClose: () => void;
}> = ({
  openKind,
  row,
  workspaceId,
  gradeId,
  academicYear,
  termNumber,
  memberLayout,
  classMembers,
  onClose,
}) => {
  const evalParsed = useMemo(
    () => parseEvaluationMemoStorage(row?.evalStorageRaw ?? ''),
    [row?.evalStorageRaw]
  );
  const [evalText, setEvalText] = useState('');
  const [evalNote, setEvalNote] = useState('');
  const [evalPlanAppliedId, setEvalPlanAppliedId] = useState<string | null>(null);
  const [evalPlanSummary, setEvalPlanSummary] = useState<string | null>(null);
  const [evalPlanElement, setEvalPlanElement] = useState<string | null>(null);
  const [evalPlanMethod, setEvalPlanMethod] = useState<string | null>(null);
  const [evalPlanPickerOpen, setEvalPlanPickerOpen] = useState(false);
  const [evalExpanded, setEvalExpanded] = useState(false);

  const [classTab, setClassTab] = useState<'lesson' | 'eval_log'>('lesson');
  const [classNoteLesson, setClassNoteLesson] = useState('');
  const [classNoteEval, setClassNoteEval] = useState('');
  const [classMemberGroupId, setClassMemberGroupId] = useState<string | null>(null);
  const [evalMemoMemberGroupId, setEvalMemoMemberGroupId] = useState<string | null>(null);
  const [assignmentMemoItemId, setAssignmentMemoItemId] = useState<string | null>(null);
  const [assignmentMemoItemTitle, setAssignmentMemoItemTitle] = useState<string | null>(null);
  const [assignmentMemoGroupId, setAssignmentMemoGroupId] = useState<string | null>(null);
  const [assignmentMemoGroupTitle, setAssignmentMemoGroupTitle] = useState<string | null>(null);
  const [assignmentMemoLinkPickerOpen, setAssignmentMemoLinkPickerOpen] = useState(false);
  const [assignmentPointsDocForLink, setAssignmentPointsDocForLink] =
    useState<AssignmentPointsNoteDoc | null>(null);
  const [classExpanded, setClassExpanded] = useState(false);
  const [evalTagGenConfirm, setEvalTagGenConfirm] = useState<{
    nextNote: string;
    sourceGroupId: string;
  } | null>(null);
  const classLessonEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const classEvalEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagGroupPick, setTagGroupPick] = useState<{
    keyword: string;
    candidates: { groupId: string; groupName: string; memberId: string; memberName: string }[];
  } | null>(null);

  const openKey = row && openKind ? `${row.annualCellKey}:${openKind}` : '';
  const [classAiDirty, setClassAiDirty] = useState(false);
  const classRecordDirty =
    useDirtySinceOpen(
      openKind === 'class' && row ? String(openKey) : null,
      JSON.stringify([
        classTab,
        classTab === 'lesson' ? classNoteLesson : classNoteEval,
        classMemberGroupId,
        assignmentMemoItemId,
      ])
    ) || classAiDirty;
  React.useEffect(() => {
    if (!row || !openKind) return;
    setError(null);
    setSaving(false);
    setEvalPlanPickerOpen(false);
    setAssignmentMemoLinkPickerOpen(false);
    setTagGroupPick(null);
    setEvalTagGenConfirm(null);
    if (openKind === 'eval') {
      setEvalText(evalParsed.text.trim());
      setEvalNote(evalParsed.note);
      setEvalPlanAppliedId(evalParsed.evaluationPlanAppliedId ?? null);
      setEvalPlanSummary(evalParsed.evaluationPlanSummary ?? null);
      setEvalPlanElement(evalParsed.evaluationPlanElement ?? null);
      setEvalPlanMethod(evalParsed.evaluationPlanMethod ?? null);
      setEvalExpanded(false);
      return;
    }
    const classParsed = parseEvaluationMemoStorage(row.classStorageRaw ?? '');
    const kind = row.progressClassRecordKind === 'eval_log' ? 'eval_log' : 'lesson';
    setClassTab(kind);
    setClassNoteLesson(kind === 'lesson' ? row.progressClassRecordNote : '');
    setClassNoteEval(kind === 'eval_log' ? row.progressClassRecordNote : '');
    const savedGroupId = row.progressClassRecordMemberGroupId;
    const defaultGroupId =
      savedGroupId && memberLayout.groups.some((g) => g.id === savedGroupId)
        ? savedGroupId
        : resolveDefaultRecordGroupId(
            memberLayout,
            loadSelectedClassGroupId(workspaceId, gradeId)
          );
    setClassMemberGroupId(defaultGroupId);
    setEvalMemoMemberGroupId(
      kind === 'eval_log' && row.progressClassRecordNote.trim() ? defaultGroupId : null
    );
    setAssignmentMemoItemId((classParsed.assignmentMemoItemId ?? '').trim() || null);
    setAssignmentMemoItemTitle((classParsed.assignmentMemoItemTitle ?? '').trim() || null);
    setAssignmentMemoGroupId((classParsed.assignmentMemoGroupId ?? '').trim() || null);
    setAssignmentMemoGroupTitle((classParsed.assignmentMemoGroupTitle ?? '').trim() || null);
    setClassExpanded(false);
    // openKey만: 부모가 row 객체를 다시 만들 때마다 연결·입력이 초기화되면 안 됨
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally openKey-gated
  }, [openKey]);

  React.useEffect(() => {
    if (openKind !== 'class' || !workspaceId.trim() || !gradeId.trim()) {
      setAssignmentPointsDocForLink(null);
      return;
    }
    let cancelled = false;
    void getAssignmentPointsNote(workspaceId, gradeId)
      .then((doc) => {
        if (!cancelled) setAssignmentPointsDocForLink(doc);
      })
      .catch(() => {
        if (!cancelled) setAssignmentPointsDocForLink(null);
      });
    return () => {
      cancelled = true;
    };
  }, [openKind, workspaceId, gradeId, openKey]);

  const sealedLabelByToken = useSealedMemoTagLabels([classNoteLesson, classNoteEval]);
  const classAiRef = React.useRef<AiRecordFeedbackPanelHandle>(null);
  const classMembersForEditor = useMemo(
    () => membersForRecordGroup(memberLayout, classMemberGroupId, classMembers),
    [classMembers, classMemberGroupId, memberLayout]
  );
  const finalizeHashtag = useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        const gid = hits[0].groupId;
        setClassMemberGroupId(gid);
        if (classTab === 'eval_log') setEvalMemoMemberGroupId(gid);
        return;
      }
      setTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [classTab, memberLayout]
  );

  const readClassNote = useCallback(() => {
    if (classTab === 'eval_log') {
      const dom = classEvalEditorRef.current?.readSerializedNoteFromDom?.();
      return dom !== undefined ? dom : classNoteEval;
    }
    const dom = classLessonEditorRef.current?.readSerializedNoteFromDom?.();
    return dom !== undefined ? dom : classNoteLesson;
  }, [classNoteEval, classNoteLesson, classTab]);

  const handleSaveEval = async () => {
    if (!row?.evalRecordTarget) {
      setError('해당 칸의 프로그램 원본 데이터가 없습니다. 프로그램 페이지에서 먼저 내용을 만들어 주세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const record_evaluation_text = serializeEvaluationMemoStorage({
        text: evalText,
        note: evalNote,
        evaluationPlanAppliedId: evalPlanAppliedId,
        evaluationPlanSummary: evalPlanSummary,
        evaluationPlanElement: evalPlanElement,
        evaluationPlanMethod: evalPlanMethod,
      });
      await saveAnnualProgressProgramRecord({
        workspaceId,
        gradeId,
        academicYear,
        termNumber,
        annualCellKey: row.annualCellKey,
        target: row.evalRecordTarget,
        patch: { record_evaluation_text },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const applyClassMemberTags = useCallback(
    (nextNote: string, sourceGroupId: string) => {
      if (classTab === 'eval_log') {
        setClassNoteEval(nextNote);
        setEvalMemoMemberGroupId(sourceGroupId);
      } else {
        setClassNoteLesson(nextNote);
      }
      setClassMemberGroupId(sourceGroupId);
      setError(null);
    },
    [classTab]
  );

  const handleGenerateClassMemberTags = useCallback(() => {
    const gid = (classMemberGroupId ?? '').trim();
    if (!gid) {
      setError('멤버 그룹을 선택한 뒤 멤버 태그 생성을 눌러 주세요.');
      return;
    }
    const members = membersForRecordGroup(memberLayout, gid, classMembers);
    if (members.length === 0) return;
    const nextNote = buildDefaultEvalMemberTagsNote(members);
    const current =
      classTab === 'eval_log'
        ? (classEvalEditorRef.current?.readSerializedNoteFromDom?.() ?? classNoteEval)
        : (classLessonEditorRef.current?.readSerializedNoteFromDom?.() ?? classNoteLesson);
    if (evalSerializedNoteHasNonTagText(current)) {
      setEvalTagGenConfirm({ nextNote, sourceGroupId: gid });
      return;
    }
    applyClassMemberTags(nextNote, gid);
  }, [
    applyClassMemberTags,
    classMemberGroupId,
    classMembers,
    classNoteEval,
    classNoteLesson,
    classTab,
    memberLayout,
  ]);

  const assignmentMemoMembersById = useMemo(() => {
    const map = new Map<string, AssignmentMemoMemberRef>();
    for (const g of memberLayout.groups) {
      const list = memberLayout.membersByGroupId[g.id] ?? [];
      const nums = getClassMemberDisplayNumbers(list);
      list.forEach((m, index) => {
        map.set(m.id, {
          id: m.id,
          name: displayClassMemberName(m).trim(),
          // 0-based 필드: 고정 번호 - 1 (번호가 없으면 순서)
          orderIndex: (nums[index] ?? index + 1) - 1,
          groupId: g.id,
        });
      });
    }
    return map;
  }, [memberLayout]);

  const classLinkedAssignmentPreview = useMemo(() => {
    const itemId = (assignmentMemoItemId ?? '').trim();
    if (!itemId) return '';
    const gid = (assignmentMemoGroupId ?? '').trim();
    const allowedMemberIds = gid
      ? new Set((memberLayout.membersByGroupId[gid] ?? []).map((m) => m.id))
      : null;
    return resolveLinkedAssignmentMemoNote({
      doc: assignmentPointsDocForLink,
      itemId,
      membersById: assignmentMemoMembersById,
      allowedMemberIds,
    });
  }, [
    assignmentMemoGroupId,
    assignmentMemoItemId,
    assignmentMemoMembersById,
    assignmentPointsDocForLink,
    memberLayout.membersByGroupId,
  ]);

  const classRecordCanDelete = useMemo(() => {
    if (openKind !== 'class') return false;
    const stored = parseEvaluationMemoStorage(row?.classStorageRaw ?? '');
    const storedText = stored.text.trim();
    if (stored.note.trim()) return true;
    if ((stored.assignmentMemoItemId ?? '').trim()) return true;
    if (storedText === 'lesson' || storedText === 'eval_log') return true;
    if (classNoteLesson.trim() || classNoteEval.trim()) return true;
    if ((assignmentMemoItemId ?? '').trim()) return true;
    return classTab === 'lesson' || classTab === 'eval_log';
  }, [
    assignmentMemoItemId,
    classNoteEval,
    classNoteLesson,
    classTab,
    openKind,
    row?.classStorageRaw,
  ]);

  const handleDeleteClass = async () => {
    if (!row?.classRecordTarget) return;
    setSaving(true);
    setError(null);
    try {
      const preservedBehavior = parseEvaluationMemoStorage(row.classStorageRaw ?? '').behaviorNote;
      await saveAnnualProgressProgramRecord({
        workspaceId,
        gradeId,
        academicYear,
        termNumber,
        annualCellKey: row.annualCellKey,
        target: row.classRecordTarget,
        patch: {
          record_class_text: serializeClassRecordCellStorage('', '', preservedBehavior),
          record_member_group_id: null,
        },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClass = async () => {
    if (!row?.classRecordTarget) {
      setError('해당 칸의 프로그램 원본 데이터가 없습니다. 프로그램 페이지에서 먼저 내용을 만들어 주세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const noteText = readClassNote().trim();
      const preservedParsed = parseEvaluationMemoStorage(row.classStorageRaw ?? '');
      const preservedBehavior = preservedParsed.behaviorNote;
      // AI 피드백은 기록 JSON 에 내장 → 기록과 함께 이동·저장·삭제
      const aiPanel = classAiRef.current;
      const commitAi = aiPanel?.capture();
      const aiFeedback = {
        ...(preservedParsed.aiFeedback ?? {}),
        ...(aiPanel ? { [classTab]: aiPanel.getEmbedValue() } : {}),
      };
      const record_class_text = serializeClassRecordCellStorage(
        classTab,
        noteText,
        preservedBehavior,
        assignmentMemoItemId,
        assignmentMemoItemTitle,
        assignmentMemoGroupId,
        assignmentMemoGroupTitle,
        aiFeedback
      );
      const explicitGroupId = (classMemberGroupId ?? '').trim() || null;
      const record_member_group_id =
        explicitGroupId || inferSingleClassRecordNoteGroupId(noteText, memberLayout);
      await saveAnnualProgressProgramRecord({
        workspaceId,
        gradeId,
        academicYear,
        termNumber,
        annualCellKey: row.annualCellKey,
        target: row.classRecordTarget,
        patch: {
          record_class_text,
          record_member_group_id,
        },
      });
      void commitAi?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!row || !openKind) return null;

  const line = contextLine(row);
  const unitLine = row.progressUnit.trim();
  const autoLines = resolveEvaluationPlanMemoAutoLines({
    evaluationPlanElement: evalPlanElement,
    evaluationPlanMethod: evalPlanMethod,
    evaluationPlanSummary: evalPlanSummary,
  });
  const hasAutoPrefix = !!(autoLines.element || autoLines.method);

  return (
    <>
      {createPortal(
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !saving) onClose();
          }}
        >
          <div
            className={
              'cp-program-note-popup cp-program-note-popup--with-head cp-program-note-popup--dashboard-lesson' +
              ((openKind === 'eval' ? evalExpanded : classExpanded) ? ' cp-program-note-popup-expanded' : '')
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-annual-progress-program-record-title"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-program-note-popup-stack">
              <div className="cp-program-note-popup-head">
                <button
                  type="button"
                  className="cp-program-note-popup-expand-btn"
                  aria-label={
                    (openKind === 'eval' ? evalExpanded : classExpanded)
                      ? '기본 크기로 축소'
                      : '확장 모달로 보기'
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() =>
                    openKind === 'eval'
                      ? setEvalExpanded((v) => !v)
                      : setClassExpanded((v) => !v)
                  }
                >
                  <img
                    src={(openKind === 'eval' ? evalExpanded : classExpanded) ? '/Minimize.svg' : '/Expand.svg'}
                    alt=""
                    aria-hidden
                    className="cp-program-note-popup-expand-icon"
                  />
                </button>
              </div>
              {openKind === 'eval' ? (
                <div className="cp-program-eval-choice-tabs">
                  <div className="cp-annual-progress-tabs cp-program-eval-choice-row-full" role="group">
                    <button
                      type="button"
                      className={'cp-annual-progress-tab cp-program-eval-tab-clear' + (!evalText.trim() ? ' is-active' : '')}
                      onClick={() => {
                        setEvalText('');
                        setEvalNote('');
                        setEvalPlanAppliedId(null);
                        setEvalPlanSummary(null);
                        setEvalPlanElement(null);
                        setEvalPlanMethod(null);
                      }}
                    >
                      <span className="cp-program-eval-tab-clear-ring" aria-hidden />
                      <span className="cp-program-eval-tab-clear-label">입력없음</span>
                    </button>
                  </div>
                  <div
                    className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-eval-choice-row-thirds"
                    role="group"
                  >
                    {EVAL_STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={
                          'cp-annual-progress-tab cp-program-eval-tab-' +
                          opt.tone +
                          (evalText.trim() === opt.value ? ' is-active' : '')
                        }
                        onClick={() => setEvalText(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="cp-program-note-popup-context-wrap">
                    <p
                      className="cp-program-note-popup-context cp-program-note-popup-context-primary"
                      id="cp-annual-progress-program-record-title"
                    >
                      {line}
                    </p>
                    {unitLine ? (
                      <p className="cp-program-note-popup-context cp-program-note-popup-context-secondary">
                        {unitLine}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="cp-program-eval-choice-tabs">
                  <div
                    className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half"
                    role="group"
                  >
                    <button
                      type="button"
                      className={
                        'cp-annual-progress-tab cp-program-class-rec-tab-lesson' +
                        (classTab === 'lesson' ? ' is-active' : '')
                      }
                      onClick={() => {
                        setError(null);
                        setClassTab('lesson');
                      }}
                    >
                      수업기록
                    </button>
                    <button
                      type="button"
                      className={
                        'cp-annual-progress-tab cp-program-class-rec-tab-eval_log' +
                        (classTab === 'eval_log' ? ' is-active' : '')
                      }
                      onClick={() => {
                        setError(null);
                        setClassTab('eval_log');
                      }}
                    >
                      평가기록
                    </button>
                  </div>
                  <div className="cp-program-note-popup-context-wrap">
                    <p
                      className="cp-program-note-popup-context cp-program-note-popup-context-primary"
                      id="cp-annual-progress-program-record-title"
                    >
                      {line}
                    </p>
                    {unitLine ? (
                      <p className="cp-program-note-popup-context cp-program-note-popup-context-secondary">
                        {unitLine}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              <div className="cp-program-note-popup-field cp-program-class-rec-memo-field">
                {openKind === 'eval' ? (
                  <>
                    <div className="cp-program-eval-plan-link-wrap">
                      {(evalPlanAppliedId ?? '').trim() ? (
                        <div className="cp-program-eval-plan-link-row">
                          <span className="cp-program-eval-plan-link-summary-text">
                            {(evalPlanSummary ?? '').trim() || '평가계획'}
                          </span>
                          <button
                            type="button"
                            className="cp-btn-secondary"
                            disabled={saving}
                            onClick={() => {
                              setEvalPlanAppliedId(null);
                              setEvalPlanSummary(null);
                              setEvalPlanElement(null);
                              setEvalPlanMethod(null);
                            }}
                          >
                            연결 해제
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="cp-program-eval-plan-import-btn"
                          disabled={saving || !gradeId.trim()}
                          onClick={() => setEvalPlanPickerOpen(true)}
                        >
                          평가계획 가져오기
                        </button>
                      )}
                    </div>
                    {hasAutoPrefix ? (
                      <div className="cp-program-eval-memo-compose">
                        <div className="cp-program-eval-memo-plan-auto" aria-readonly="true">
                          {autoLines.element ? <p>{autoLines.element}</p> : null}
                          {autoLines.method ? <p>{autoLines.method}</p> : null}
                        </div>
                        <textarea
                          rows={4}
                          value={evalNote}
                          onChange={(e) => setEvalNote(e.target.value)}
                          placeholder="평가 메모"
                          disabled={saving}
                        />
                      </div>
                    ) : (
                      <textarea
                        rows={6}
                        value={evalNote}
                        onChange={(e) => setEvalNote(e.target.value)}
                        placeholder="평가 메모"
                        disabled={saving}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <div
                      className={
                        'cp-class-rec-member-toolbar' +
                        (classTab === 'eval_log'
                          ? ' cp-class-rec-member-toolbar-eval'
                          : ' cp-class-rec-member-toolbar-lesson')
                      }
                    >
                      <RecordMemberGroupSelect
                        id="cp-annual-progress-class-member-group"
                        layout={memberLayout}
                        value={classMemberGroupId}
                        requireGroup
                        onChange={setClassMemberGroupId}
                        disabled={saving}
                        wrapClassName="cp-class-rec-member-group-select-half"
                      />
                      <button
                        type="button"
                        className="cp-btn-secondary cp-class-rec-member-tag-gen-btn"
                        disabled={
                          saving ||
                          !(classMemberGroupId ?? '').trim() ||
                          !firstRecordGroupId(memberLayout)
                        }
                        onClick={handleGenerateClassMemberTags}
                      >
                        멤버 태그 생성
                      </button>
                    </div>
                    <div className="cp-program-eval-plan-link-wrap">
                      {(assignmentMemoItemId ?? '').trim() ? (
                        <div className="cp-program-assignment-memo-link-bar">
                          <span className="cp-program-assignment-memo-link-title">
                            {(assignmentMemoItemTitle ?? '').trim() || '과제 기록'}
                            {(assignmentMemoGroupTitle ?? '').trim()
                              ? ` · ${(assignmentMemoGroupTitle ?? '').trim()}`
                              : ''}
                          </span>
                          <button
                            type="button"
                            className="cp-program-assignment-memo-unlink-btn"
                            disabled={saving}
                            onClick={() => {
                              setAssignmentMemoItemId(null);
                              setAssignmentMemoItemTitle(null);
                              setAssignmentMemoGroupId(null);
                              setAssignmentMemoGroupTitle(null);
                            }}
                          >
                            연결 해제
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="cp-program-eval-plan-import-btn"
                          disabled={saving || !gradeId.trim()}
                          onClick={() => setAssignmentMemoLinkPickerOpen(true)}
                        >
                          과제 기록 가져오기
                        </button>
                      )}
                    </div>
                    {(assignmentMemoItemId ?? '').trim() ? (
                      <div
                        className="cp-program-assignment-memo-field-display"
                        aria-label={classTab === 'eval_log' ? '평가기록 메모' : '수업기록 메모'}
                      >
                        {classLinkedAssignmentPreview
                          ? renderNoteWithMemberTags(classLinkedAssignmentPreview, classMembers, {
                              applyBulkMemberTagRules: false,
                            })
                          : (
                            <span className="cp-program-assignment-memo-field-empty">
                              연결된 과제에 표시할 메모가 없습니다.
                            </span>
                          )}
                      </div>
                    ) : classTab === 'lesson' ? (
                      <ClassRecordMemoNoteEditor
                        key={`${row.annualCellKey}:lesson`}
                        ref={classLessonEditorRef}
                        value={classNoteLesson}
                        onChange={setClassNoteLesson}
                        members={classMembersForEditor}
                        applyBulkMemberTagRules={!!classMemberGroupId}
                        sealedLabelByToken={sealedLabelByToken}
                        onHashtagTagFinalized={finalizeHashtag}
                        placeholder="수업기록 메모"
                      />
                    ) : (
                      <ClassRecordMemoNoteEditor
                        key={`${row.annualCellKey}:eval`}
                        ref={classEvalEditorRef}
                        value={classNoteEval}
                        onChange={(note) => {
                          setClassNoteEval(note);
                          if (!note.trim()) setEvalMemoMemberGroupId(null);
                        }}
                        members={classMembersForEditor}
                        applyBulkMemberTagRules={!!(evalMemoMemberGroupId ?? '').trim()}
                        sealedLabelByToken={sealedLabelByToken}
                        onHashtagTagFinalized={finalizeHashtag}
                        placeholder="평가기록 메모"
                      />
                    )}
                  </>
                )}
              </div>
              {error ? (
                <p className="cp-program-class-rec-modal-error" role="alert">
                  {error}
                </p>
              ) : null}
              {openKind === 'class' ? (
                <AiRecordFeedbackPanel
                  ref={classAiRef}
                  workspaceId={workspaceId}
                  recordKey={row.annualCellKey}
                  kind={classTab}
                  onDirtyChange={setClassAiDirty}
                  embeddedText={parseEvaluationMemoStorage(row.classStorageRaw ?? '').aiFeedback?.[classTab] ?? null}
                  note={classTab === 'lesson' ? classNoteLesson : classNoteEval}
                  contextLines={[
                    `편제: ${contextLine(row)}`,
                    row.progressUnit && `단원: ${row.progressUnit}`,
                    row.progressUnitNote && `성취기준: ${row.progressUnitNote}`,
                    row.progressLearning && `학습내용: ${row.progressLearning}`,
                    row.progressLearningNote && `학습내용 메모: ${row.progressLearningNote}`,
                  ]}
                  members={classMembersForEditor}
                  allMembers={classMembers}
                  disabled={saving}
                />
              ) : null}
              <div className="cp-program-note-popup-actions">
                <button
                  type="button"
                  className="cp-program-note-confirm-btn"
                  onClick={onClose}
                  disabled={saving}
                >
                  닫기
                </button>
                <button
                  type="button"
                  className="cp-program-note-confirm-btn cp-program-note-confirm-btn--primary cp-record-save-btn"
                  onClick={() => {
                    void (openKind === 'eval' ? handleSaveEval() : handleSaveClass());
                  }}
                  disabled={saving || (openKind === 'class' && !classRecordDirty)}
                >
                  {saving ? '저장 중…' : openKind === 'class' ? (classRecordDirty ? '저장' : '변경사항 없음') : '확인'}
                </button>
                {openKind === 'class' ? (
                  <DeleteHoldButton
                    disabled={saving || !classRecordCanDelete}
                    disabledTitle="삭제할 수업·평가 기록이 없습니다"
                    disabledAriaLabel="삭제할 수업·평가 기록이 없습니다"
                    ariaLabel="수업·평가 기록 삭제 (길게 누르기)"
                    onDelete={() => {
                      void classAiRef.current?.remove(['lesson', 'eval_log']);
                      void handleDeleteClass();
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      <EvaluationPlanAppliedPickerModal
        open={evalPlanPickerOpen}
        workspaceId={workspaceId}
        gradeId={gradeId}
        onClose={() => setEvalPlanPickerOpen(false)}
        onPick={(snap) => {
          setEvalPlanAppliedId(snap.id);
          setEvalPlanSummary(formatPlanSummary(snap.plan));
          setEvalPlanElement(snap.plan.evaluation_element_text ?? '');
          setEvalPlanMethod(snap.plan.evaluation_method_text ?? '');
          setEvalPlanPickerOpen(false);
        }}
      />
      <AssignmentMemoLinkPickerModal
        open={assignmentMemoLinkPickerOpen}
        workspaceId={workspaceId}
        gradeId={gradeId}
        membersById={assignmentMemoMembersById}
        memberLayout={memberLayout}
        doc={assignmentPointsDocForLink}
        initialGroupId={classMemberGroupId}
        onClose={() => setAssignmentMemoLinkPickerOpen(false)}
        onPick={(candidate) => {
          setAssignmentMemoItemId(candidate.itemId);
          setAssignmentMemoItemTitle(candidate.title);
          setAssignmentMemoGroupId(candidate.groupId);
          setAssignmentMemoGroupTitle(candidate.groupLabel || null);
          if (candidate.groupId) {
            setClassMemberGroupId(candidate.groupId);
            if (classTab === 'eval_log') setEvalMemoMemberGroupId(candidate.groupId);
          }
          setAssignmentMemoLinkPickerOpen(false);
        }}
      />
      {tagGroupPick
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setTagGroupPick(null);
              }}
            >
              <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
                <h3>멤버 그룹 선택</h3>
                <p className="page-desc">
                  &quot;{tagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할 학생을 선택해
                  주세요.
                </p>
                <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
                  {tagGroupPick.candidates.map((c) => (
                    <button
                      key={`${c.groupId}-${c.memberId}`}
                      type="button"
                      className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                      onClick={() => {
                        setClassMemberGroupId(c.groupId);
                        setTagGroupPick(null);
                      }}
                    >
                      {c.groupName} · {c.memberName}
                    </button>
                  ))}
                </div>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setTagGroupPick(null)}>
                    취소
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {evalTagGenConfirm
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setEvalTagGenConfirm(null);
              }}
            >
              <div
                className="cp-modal"
                role="dialog"
                aria-modal="true"
                onMouseDown={(ev) => ev.stopPropagation()}
              >
                <h3>멤버 태그 생성</h3>
                <p className="page-desc">
                  태그(#이름)가 아닌 입력 내용은 모두 초기화되고, 선택한 그룹의 멤버 태그만 남습니다. 계속할까요?
                </p>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setEvalTagGenConfirm(null)}>
                    취소
                  </button>
                  <button
                    type="button"
                    className="cp-btn-primary"
                    onClick={() => {
                      applyClassMemberTags(evalTagGenConfirm.nextNote, evalTagGenConfirm.sourceGroupId);
                      setEvalTagGenConfirm(null);
                    }}
                  >
                    초기화 후 생성
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default AnnualProgressProgramRecordModals;
